import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Lock, CreditCard, Truck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Textarea } from '../components/ui/textarea';
import { useApp } from '../context/AppContext';
import { formatPrice } from '../lib/utils';
import { toast } from 'sonner';
import { usePageTitle } from '../hooks/usePageTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CheckoutPage() {
  const { cart, cartId, clearCart } = useApp();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    shipping_address: '',
    shipping_city: '',
    shipping_postal_code: '',
    shipping_country: 'New Zealand',
    notes: ''
  });

  const shippingCost = cart.shipping_cost ?? (cart.subtotal >= 100 ? 0 : 10);
  const gstAmount = cart.gst_amount ?? Math.round((cart.subtotal + shippingCost) * 0.15 * 100) / 100;
  const total = cart.total ?? (cart.subtotal + shippingCost + gstAmount);
  usePageTitle('Checkout');

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    const required = ['customer_name', 'customer_email', 'customer_phone', 'shipping_address', 'shipping_city', 'shipping_postal_code'];
    for (const field of required) {
      if (!formData[field].trim()) {
        toast.error(`Please fill in ${field.replace('_', ' ')}`);
        return false;
      }
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.customer_email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    if (cart.items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/checkout/create-session`, {
        ...formData,
        cart_id: cartId,
        payment_method: paymentMethod
      }, {
        headers: {
          'Origin': window.location.origin
        }
      });

      if (paymentMethod === 'stripe' && response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else {
        // For PayPal, redirect to order confirmation (placeholder)
        toast.success('Order created! PayPal integration coming soon.');
        navigate(`/order-success?order_id=${response.data.order_id}`);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Checkout failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (cart.items.length === 0) {
    return (
      <div className="page-transition min-h-[60vh] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <h1 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Your cart is empty
          </h1>
          <p className="text-muted-foreground mb-8">
            Add some items to your cart before checking out.
          </p>
          <Link to="/shop">
            <Button className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-full px-8">
              Browse Products
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-transition">
      {/* Header */}
      <div className="bg-[#0B1120] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="breadcrumb mb-4">
            <Link to="/" className="text-white/60 hover:text-white">Home</Link>
            <span className="text-white/40">/</span>
            <Link to="/cart" className="text-white/60 hover:text-white">Cart</Link>
            <span className="text-white/40">/</span>
            <span className="text-white">Checkout</span>
          </nav>
          <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="checkout-title">
            Checkout
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Form */}
            <div className="lg:col-span-2 space-y-8">
              {/* Contact Information */}
              <div className="bg-white rounded-xl border border-border p-6">
                <h2 className="text-xl font-bold mb-6 flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  <span className="w-8 h-8 rounded-full bg-[#EA580C] text-white text-sm flex items-center justify-center">1</span>
                  Contact Information
                </h2>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Label htmlFor="customer_name">Full Name *</Label>
                    <Input
                      id="customer_name"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleInputChange}
                      placeholder="John Smith"
                      required
                      className="mt-1"
                      data-testid="customer-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="customer_email">Email *</Label>
                    <Input
                      id="customer_email"
                      name="customer_email"
                      type="email"
                      value={formData.customer_email}
                      onChange={handleInputChange}
                      placeholder="john@example.com"
                      required
                      className="mt-1"
                      data-testid="customer-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="customer_phone">Phone *</Label>
                    <Input
                      id="customer_phone"
                      name="customer_phone"
                      type="tel"
                      value={formData.customer_phone}
                      onChange={handleInputChange}
                      placeholder="+64 21 123 4567"
                      required
                      className="mt-1"
                      data-testid="customer-phone-input"
                    />
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="bg-white rounded-xl border border-border p-6">
                <h2 className="text-xl font-bold mb-6 flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  <span className="w-8 h-8 rounded-full bg-[#EA580C] text-white text-sm flex items-center justify-center">2</span>
                  Shipping Address
                </h2>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Label htmlFor="shipping_address">Street Address *</Label>
                    <Input
                      id="shipping_address"
                      name="shipping_address"
                      value={formData.shipping_address}
                      onChange={handleInputChange}
                      placeholder="123 Queen Street"
                      required
                      className="mt-1"
                      data-testid="shipping-address-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="shipping_city">City *</Label>
                    <Input
                      id="shipping_city"
                      name="shipping_city"
                      value={formData.shipping_city}
                      onChange={handleInputChange}
                      placeholder="Auckland"
                      required
                      className="mt-1"
                      data-testid="shipping-city-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="shipping_postal_code">Postal Code *</Label>
                    <Input
                      id="shipping_postal_code"
                      name="shipping_postal_code"
                      value={formData.shipping_postal_code}
                      onChange={handleInputChange}
                      placeholder="1010"
                      required
                      className="mt-1"
                      data-testid="shipping-postal-input"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="shipping_country">Country</Label>
                    <Input
                      id="shipping_country"
                      name="shipping_country"
                      value={formData.shipping_country}
                      onChange={handleInputChange}
                      disabled
                      className="mt-1 bg-muted"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-white rounded-xl border border-border p-6">
                <h2 className="text-xl font-bold mb-6 flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  <span className="w-8 h-8 rounded-full bg-[#EA580C] text-white text-sm flex items-center justify-center">3</span>
                  Payment Method
                </h2>
                
                <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-4">
                  <div className={`flex items-center space-x-4 p-4 rounded-lg border-2 transition-colors cursor-pointer ${paymentMethod === 'stripe' ? 'border-[#EA580C] bg-[#EA580C]/5' : 'border-border'}`}>
                    <RadioGroupItem value="stripe" id="stripe" data-testid="payment-stripe" />
                    <Label htmlFor="stripe" className="flex-1 cursor-pointer">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">Credit/Debit Card</p>
                          <p className="text-sm text-muted-foreground">Visa, Mastercard, Google Pay</p>
                        </div>
                        <CreditCard className="h-6 w-6 text-muted-foreground" />
                      </div>
                    </Label>
                  </div>
                  
                  <div className={`flex items-center space-x-4 p-4 rounded-lg border-2 transition-colors cursor-pointer ${paymentMethod === 'paypal' ? 'border-[#EA580C] bg-[#EA580C]/5' : 'border-border'}`}>
                    <RadioGroupItem value="paypal" id="paypal" data-testid="payment-paypal" />
                    <Label htmlFor="paypal" className="flex-1 cursor-pointer">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">PayPal</p>
                          <p className="text-sm text-muted-foreground">Pay with your PayPal account</p>
                        </div>
                        <span className="text-[#003087] font-bold text-lg">PayPal</span>
                      </div>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Order Notes */}
              <div className="bg-white rounded-xl border border-border p-6">
                <h2 className="text-xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Order Notes (Optional)
                </h2>
                <Textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Any special instructions for your order..."
                  rows={3}
                  data-testid="order-notes-input"
                />
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl border border-border p-6 sticky top-24" data-testid="checkout-summary">
                <h2 className="text-xl font-bold mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Order Summary
                </h2>

                {/* Items */}
                <div className="space-y-4 mb-6 max-h-64 overflow-y-auto">
                  {cart.items.map((item) => (
                    <div key={`${item.product_id}-${item.variant_id}`} className="flex gap-3">
                      <img
                        src={item.product?.images?.[0] || 'https://images.unsplash.com/photo-1559709451-1b5dc3c0038c?w=100'}
                        alt={item.product?.name}
                        className="w-16 h-16 rounded-lg object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm line-clamp-1">{item.product?.name}</p>
                        <p className="text-xs text-muted-foreground">
                          Qty: {item.quantity}
                          {item.variant_label && ` • ${item.variant_label}`}
                        </p>
                        <p className="text-sm font-medium">{formatPrice(item.item_total)}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-border pt-4 space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="font-medium">{formatPrice(cart.subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Shipping</span>
                    <span className="font-medium">
                      {shippingCost === 0 ? (
                        <span className="text-green-600">Free</span>
                      ) : (
                        formatPrice(shippingCost)
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">GST (15%)</span>
                    <span className="font-medium" data-testid="checkout-gst">{formatPrice(gstAmount)}</span>
                  </div>
                </div>

                <div className="border-t border-border pt-4 mb-6">
                  <div className="flex justify-between">
                    <span className="text-lg font-semibold">Total</span>
                    <span className="text-lg font-bold" data-testid="checkout-total">
                      {formatPrice(total)}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Includes {formatPrice(gstAmount)} GST</p>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-full py-6 text-lg font-medium btn-primary"
                  data-testid="place-order-btn"
                >
                  {loading ? (
                    'Processing...'
                  ) : (
                    <>
                      <Lock className="mr-2 h-5 w-5" />
                      Place Order
                    </>
                  )}
                </Button>

                <p className="text-xs text-center text-muted-foreground mt-4">
                  By placing your order, you agree to our Terms of Service and Privacy Policy.
                </p>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
